/**
 * =============================================================================
 * Componente: PasswordRequirements
 * Versão: 1.0.0
 *
 * Descrição:
 *   Checklist em tempo real dos requisitos de senha forte, exibido logo
 *   abaixo do campo "Nova senha" nas telas de definição/redefinição.
 *   Cada regra mostra um check verde quando atendida e um X cinza quando
 *   pendente, orientando o usuário ANTES do submit (evita erro do backend).
 *
 *   Reaproveita as regras centralizadas em `src/lib/passwordRules.ts`.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-04 v1.0.0 - Versão inicial.
 * =============================================================================
 */
import { Check, X } from 'lucide-react';
import { evaluatePasswordRules } from '@/lib/passwordRules';
import { cn } from '@/lib/utils';

interface PasswordRequirementsProps {
  /** Senha digitada atualmente (avalia as regras em tempo real) */
  password: string;
  /** Classes extras para o container (ex.: ajuste de cor por tema) */
  className?: string;
}

export function PasswordRequirements({ password, className }: PasswordRequirementsProps) {
  const checks = evaluatePasswordRules(password);

  return (
    <ul className={cn('space-y-1 pt-1', className)} aria-live="polite">
      {checks.map((check) => (
        <li
          key={check.id}
          className={cn(
            'flex items-center gap-1.5 text-xs transition-colors',
            check.met ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground',
          )}
        >
          {check.met ? (
            <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
          ) : (
            <X className="h-3.5 w-3.5 shrink-0" aria-hidden />
          )}
          <span>{check.label}</span>
        </li>
      ))}
    </ul>
  );
}
